import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  api,
  type ColetaDeTransportadora,
  type ColetaDoOperador,
  type ColetaFechada,
  type PacoteDaColeta,
} from "../api";
import { useBatch } from "../batch";
import { BolinhaIcone, QrIcon } from "../components";
import { armAudio } from "../feedback";
import { engineAvailable, startScanning } from "../scanner";
import { ScanScreen } from "../ScanScreen";

/// A câmera só para LER, sem o fluxo de lote e entrega do `ScanScreen`.
///
/// A transferência não confere pacote nem grava nada na hora — só precisa do
/// texto da etiqueta, que é o que `startScanning` já devolve sozinho. Usar o
/// `ScanScreen` inteiro aqui traria a tela de escolher conta e o flash de
/// "Adicionado" de um fluxo que não é este: bipar para transferir não é
/// entregar.
function LeitorDeTransferencia({
  onLido,
  onFechar,
  dica = "Escaneie a etiqueta para transferir",
}: {
  onLido: (code: string) => void;
  onFechar: () => void;
  /// O texto muda conforme quem chama — "transferir" não faz sentido pra
  /// quem tá procurando um pacote pra liberar de uma coleta já fechada.
  dica?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let parar: (() => void) | undefined;
    let cancelado = false;

    void startScanning(
      video,
      (code) => {
        if (cancelado) return;
        cancelado = true; // Só a primeira leitura conta — o resto é o mesmo pacote parado na frente da câmera.
        if ("vibrate" in navigator) navigator.vibrate(45);
        parar?.();
        onLido(code);
      },
      setErro
    ).then((fn) => {
      if (cancelado) fn();
      else parar = fn;
    });

    return () => {
      cancelado = true;
      parar?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Direto no <body>: este leitor é chamado de dentro de `Finalizar`, que por
  // sua vez é chamada de dentro do <main> da tela de Bipar — que rola
  // (`overflow-y: auto`). No Safari do iPhone, um `position: fixed` preso
  // dentro de um contêiner que rola fica cortado pelos limites daquele
  // contêiner em vez de cobrir a tela toda — o menu de baixo aparecia por
  // cima da câmera. O portal tira a árvore de onde ela foi chamada e bota no
  // <body>, imune a qualquer aninhamento futuro — inclusive um componente
  // reutilizável como este, que não controla onde vai ser chamado.
  return createPortal(
    <div className="scan-screen">
      <video ref={videoRef} muted playsInline />

      <div className="viewfinder" aria-hidden="true">
        <span className="c tl" />
        <span className="c tr" />
        <span className="c bl" />
        <span className="c br" />
      </div>

      <button className="scan-back" onClick={onFechar} aria-label="Voltar">
        ←
      </button>

      <div className="scan-hint">{dica}</div>

      {engineAvailable() === "jsqr" && (
        <div className="scan-warn">
          Este aparelho lê só QR. A etiqueta da Shopee é código de barras — digite o código no
          campo.
        </div>
      )}

      {erro && (
        <div className="scan-warn" style={{ background: "var(--dep-danger)", color: "#fff" }}>
          {erro}
        </div>
      )}
    </div>,
    document.body
  );
}

/// Os motoristas de sempre.
///
/// Escritos aqui, e não numa tabela: são quatro, mudam de ano em ano, e
/// cadastrar gente só para escrever um nome seria uma tela a mais para manter.
/// Quem vier fora da lista entra em "Outro" — o campo de texto existe
/// justamente porque a lista vai estar errada algum dia.
const MOTORISTAS = ["Wagner", "Vitor", "Beto", "Mario"];

/// A janela de confirmar a coleta, no celular.
///
/// Começa pela pergunta da porta: qual caminhão chegou. Escolhido ele, as
/// caixas dele aparecem sozinhas — quem está com o motorista do J3 não deveria
/// ter de procurar "Shopee" numa lista para desmarcar.
///
/// Existe aqui, e não só no painel, porque quem está com o motorista está com
/// o celular na mão, não sentado no computador — e obrigar a ir até a bancada
/// significaria confirmar de memória, depois.
function Finalizar({
  transportadoras,
  total,
  aoFechar,
}: {
  transportadoras: ColetaDeTransportadora[];
  total: number;
  aoFechar: () => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [carrierId, setCarrierId] = useState("");
  const [escolhidas, setEscolhidas] = useState<string[]>([]);
  const [motorista, setMotorista] = useState("");
  const [outroMotorista, setOutroMotorista] = useState("");
  const [observacao, setObservacao] = useState("");

  const [transferidos, setTransferidos] = useState<PacoteDaColeta[]>([]);
  const [lido, setLido] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [avisoDaBipagem, setAvisoDaBipagem] = useState<string | null>(null);
  const [camera, setCamera] = useState(false);

  const abrir = () => {
    setErro(null);
    setCarrierId("");
    setEscolhidas([]);
    setMotorista("");
    setOutroMotorista("");
    setObservacao("");
    setTransferidos([]);
    setLido("");
    setAvisoDaBipagem(null);
    setCamera(false);
    setAberto(true);
  };

  const daTransportadora = transportadoras.find((t) => t.carrierId === carrierId);

  /// Escolher o caminhão já marca tudo o que é dele: levar tudo é o caso
  /// comum, e obrigar a marcar três caixas toda vez cobraria por um caso raro
  /// o preço de todos os dias.
  const escolherTransportadora = (id: string) => {
    setCarrierId(id);
    setTransferidos([]);
    setAvisoDaBipagem(null);

    const dela = transportadoras.find((t) => t.carrierId === id);
    setEscolhidas((dela?.porPlataforma ?? []).map((p) => p.plataforma));
  };

  const alternar = (nome: string) =>
    setEscolhidas((atual) =>
      atual.includes(nome) ? atual.filter((n) => n !== nome) : [...atual, nome]
    );

  /// A transferência: bipar a etiqueta do pacote que sobe neste caminhão.
  ///
  /// Só LÊ — nada muda até o Confirmar, então uma leitura errada não vira
  /// dado. É a mesma ideia do lote da bipagem: confere na hora, grava no fim.
  const biparTransferencia = async (code: string) => {
    const texto = code.trim();
    if (!texto) return;

    setBuscando(true);
    setAvisoDaBipagem(null);
    try {
      const { pacote } = await api.pacoteDaColeta(texto);

      if (transferidos.some((p) => p.id === pacote.id)) {
        setAvisoDaBipagem(`${pacote.code} já está na lista.`);
      } else {
        setTransferidos((atual) => [pacote, ...atual]);
        // A divisão entra marcada: o pacote foi bipado justamente para sair
        // neste caminhão, e deixá-la desmarcada faria o total não subir.
        setEscolhidas((atual) =>
          atual.includes(pacote.divisao) ? atual : [...atual, pacote.divisao]
        );
      }

      setLido("");
    } catch (err) {
      setAvisoDaBipagem(err instanceof Error ? err.message : "Não achei este pacote.");
    } finally {
      setBuscando(false);
    }
  };

  // Contada na tela: quem confere precisa ver o número mudar no instante em
  // que bipa, e uma ida ao servidor a cada leitura deixaria o total sempre um
  // passo atrás do dedo.
  const contagem = (() => {
    const base = new Map<string, number>();
    for (const p of daTransportadora?.porPlataforma ?? []) base.set(p.plataforma, p.pacotes);
    for (const t of transferidos) base.set(t.divisao, (base.get(t.divisao) ?? 0) + 1);
    return [...base.entries()].map(([plataforma, pacotes]) => ({ plataforma, pacotes }));
  })();

  const aSair = contagem
    .filter((p) => escolhidas.includes(p.plataforma))
    .reduce((s, p) => s + p.pacotes, 0);

  const nomeDoMotorista = motorista === "Outro" ? outroMotorista.trim() : motorista;

  const confirmar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      await api.fecharColeta({
        carrierId,
        divisoes: escolhidas,
        motorista: nomeDoMotorista || undefined,
        observacao: observacao.trim() || undefined,
        transferir: transferidos.map((p) => p.id),
      });
      aoFechar();
      setAberto(false);
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível fechar.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <button
        className="btn-coleta"
        // Sem pacote não há coleta para fechar. Desabilitado em vez de
        // escondido: um botão que some sozinho é mais confuso que um apagado.
        disabled={total === 0}
        onClick={abrir}
      >
        Finalizar coleta
        {total > 0 && <span className="quantos">{total}</span>}
      </button>

      {aberto &&
        // Portal direto pro <body>: esta janela é chamada de dentro do
        // <main> da tela de Bipar, que rola (`overflow-y: auto`). No Safari
        // do iPhone, um `position: fixed` preso num contêiner que rola fica
        // cortado pelos limites daquele contêiner — o menu de baixo aparecia
        // por cima da janela de finalizar coleta. O portal escapa disso.
        createPortal(
        // Fechar pelo fundo — mas não por toque dentro: um encostar de dedo no
        // meio da conferência faria recomeçar tudo com o motorista esperando.
        <div className="sobreposicao" onClick={() => setAberto(false)}>
          <div className="janela" onClick={(e) => e.stopPropagation()}>
            <h2>Finalizar coleta</h2>

            {erro && <p className="erro">{erro}</p>}

            {/* Passo 1: quem chegou. Nada mais aparece antes disto — a pergunta
                na porta é sempre "quem é o motorista?", e responder ela
                primeiro é o que faz o resto caber numa olhada. */}
            <h3 className="rotulo">Qual caminhão está na porta?</h3>
            <div className="motoristas">
              {transportadoras.map((t) => (
                <button
                  key={t.carrierId}
                  type="button"
                  className={`chip-escolha${carrierId === t.carrierId ? " marcada" : ""}`}
                  onClick={() => escolherTransportadora(t.carrierId)}
                >
                  {t.transportadora}
                  <span className="quantos-chip">{t.pacotes}</span>
                </button>
              ))}
            </div>

            {carrierId && (
              <>
                <div className="bolinhas">
                  {contagem.map((p) => (
                    <button
                      key={p.plataforma}
                      type="button"
                      className={`bolinha${escolhidas.includes(p.plataforma) ? " marcada" : ""}`}
                      onClick={() => alternar(p.plataforma)}
                    >
                      <BolinhaIcone nome={p.plataforma} />
                      <span className="quanto">{p.pacotes}</span>
                    </button>
                  ))}
                </div>

                {/* Transferência: a caixa que sobe num caminhão que não é o
                    dela. Bipada, e não escolhida numa lista — na porta a
                    pessoa tem a etiqueta na mão, e procurar o código numa
                    lista de trinta é mais lento e mais fácil de errar. */}
                <div className="transferencia">
                  <h3 className="rotulo">Transferir pacote para este caminhão</h3>
                  <input
                    className="campo-texto"
                    placeholder="Bipe a etiqueta"
                    value={lido}
                    disabled={buscando}
                    onChange={(e) => setLido(e.target.value)}
                    onKeyDown={(e) => {
                      // O leitor digita e aperta Enter; no teclado do celular
                      // é o "ir" do canto.
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void biparTransferencia(lido);
                      }
                    }}
                  />

                  {/* A câmera é a via principal aqui — quem está na porta tem
                      a etiqueta na mão, não um leitor de código de barras
                      dedicado. O campo de texto acima continua existindo para
                      quem tem um leitor físico ligado ao celular (ele "digita"
                      no campo) ou para o aparelho que só lê QR e precisa
                      digitar a etiqueta da Shopee na mão. */}
                  <button
                    type="button"
                    className="btn-bipar-transferencia"
                    onClick={() => {
                      armAudio();
                      setCamera(true);
                    }}
                  >
                    <QrIcon />
                    Bipar
                  </button>

                  {avisoDaBipagem && <p className="erro">{avisoDaBipagem}</p>}

                  {transferidos.map((p) => (
                    <div key={p.id} className="transferido">
                      <b>{p.divisao}</b>
                      <span className="codigo">{p.code}</span>
                      <button
                        type="button"
                        className="tirar"
                        onClick={() =>
                          setTransferidos((atual) => atual.filter((x) => x.id !== p.id))
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                <h3 className="rotulo">Motorista</h3>
                <div className="motoristas">
                  {[...MOTORISTAS, "Outro"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`chip-escolha${motorista === m ? " marcada" : ""}`}
                      onClick={() => setMotorista(motorista === m ? "" : m)}
                    >
                      {m}
                    </button>
                  ))}
                </div>

                {motorista === "Outro" && (
                  <input
                    className="campo-texto"
                    placeholder="Nome de quem veio"
                    value={outroMotorista}
                    maxLength={60}
                    onChange={(e) => setOutroMotorista(e.target.value)}
                  />
                )}

                <h3 className="rotulo">Observação (opcional)</h3>
                <textarea
                  className="campo-texto"
                  placeholder="Algo pra lembrar sobre esta passagem"
                  value={observacao}
                  maxLength={240}
                  rows={2}
                  onChange={(e) => setObservacao(e.target.value)}
                />

                <div className="total-da-coleta">
                  <span>Total a sair</span>
                  <strong>{aSair}</strong>
                </div>
              </>
            )}

            {/* Sem motorista não dá pra confirmar — o número fechado é a
                conferência com ALGUÉM específico. Pedido do Abner em 22/09. */}
            {carrierId && !nomeDoMotorista && (
              <p className="erro">Selecione quem veio buscar antes de confirmar.</p>
            )}

            <div className="acoes">
              <button
                className="primario"
                // Confirmar zero criaria uma coleta vazia no histórico, e o
                // histórico é a memória de quem veio buscar o quê. Sem
                // motorista, ninguém saberia com quem conferir depois.
                disabled={salvando || !carrierId || aSair === 0 || !nomeDoMotorista}
                onClick={() => void confirmar()}
              >
                {salvando ? "Confirmando…" : "Confirmar"}
              </button>
              <button disabled={salvando} onClick={() => setAberto(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {camera && (
        <LeitorDeTransferencia
          onFechar={() => setCamera(false)}
          onLido={(code) => {
            setCamera(false);
            void biparTransferencia(code);
          }}
        />
      )}
    </>
  );
}

/// Tira um pacote de dentro de uma coleta já fechada — o motorista esqueceu
/// na prateleira e só se descobriu depois de fechar. O número da coleta não
/// muda; o pacote volta a ficar disponível pra próxima passagem. Mesma ideia
/// do painel, existe aqui porque quem descobre o esquecimento geralmente está
/// com o celular na mão, na bancada — pedido do Abner em 22/09.
function LiberarPacoteDeColeta({ coletaId, onLiberado }: { coletaId: string; onLiberado: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [pacotes, setPacotes] = useState<PacoteDaColeta[]>([]);
  const [escolhido, setEscolhido] = useState<PacoteDaColeta | null>(null);
  const [bipadoPelaTransportadora, setBipadoPelaTransportadora] = useState<boolean | null>(null);
  const [observacao, setObservacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [camera, setCamera] = useState(false);
  const [buscando, setBuscando] = useState(false);

  const abrir = () => {
    setAberto(true);
    setEscolhido(null);
    setBipadoPelaTransportadora(null);
    setObservacao("");
    setErro(null);
    setCamera(false);
    setCarregando(true);
    void api
      .pacotesDaColeta(coletaId)
      .then((r) => setPacotes(r.pacotes))
      .catch((e) => setErro(e instanceof Error ? e.message : "Não foi possível carregar."))
      .finally(() => setCarregando(false));
  };

  /// Achar direto pelo código bipado — a lista inteira só entra quando isto
  /// falha, ou pra quem prefere procurar visualmente.
  const biparPacote = async (code: string) => {
    setBuscando(true);
    setErro(null);
    try {
      const { pacote } = await api.pacoteNaColeta(coletaId, code);
      setEscolhido(pacote);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não achei este pacote nesta coleta.");
    } finally {
      setBuscando(false);
    }
  };

  const confirmar = async () => {
    if (!escolhido || bipadoPelaTransportadora === null) return;
    setSalvando(true);
    setErro(null);
    try {
      await api.liberarPacoteDeColeta(escolhido.id, bipadoPelaTransportadora, observacao.trim() || undefined);
      onLiberado();
      setAberto(false);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível liberar.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <button type="button" className="liberar-pacote" onClick={abrir}>
        Liberar pacote…
      </button>

      {aberto &&
        createPortal(
          <div className="sobreposicao" onClick={() => !salvando && setAberto(false)}>
            <div className="janela" onClick={(e) => e.stopPropagation()}>
              <h2>Liberar pacote</h2>
              <p className="nota">
                Pra quando o motorista deixou uma caixa pra trás. O pacote volta a ficar em aberto — o
                número desta coleta não muda.
              </p>

              {erro && <p className="erro">{erro}</p>}

              {carregando ? (
                <p>Carregando…</p>
              ) : !escolhido ? (
                <>
                  <button
                    type="button"
                    className="btn-bipar-transferencia"
                    disabled={buscando}
                    onClick={() => {
                      armAudio();
                      setCamera(true);
                    }}
                  >
                    <QrIcon />
                    {buscando ? "Procurando…" : "Bipar"}
                  </button>

                  {pacotes.length === 0 ? (
                    <p className="erro">Nenhum pacote nesta coleta.</p>
                  ) : (
                    pacotes.map((p) => (
                      <div key={p.id} className="transferido">
                        <b>{p.divisao}</b>
                        <span className="codigo">{p.code}</span>
                        <button type="button" className="liberar-item" onClick={() => setEscolhido(p)}>
                          Liberar
                        </button>
                      </div>
                    ))
                  )}
                </>
              ) : (
                <>
                  <p>
                    Pacote <span className="codigo">{escolhido.code}</span> ({escolhido.conta ?? "—"} ·{" "}
                    {escolhido.divisao})
                  </p>

                  <h3 className="rotulo">A transportadora chegou a bipar este pacote?</h3>
                  <div className="motoristas">
                    <button
                      type="button"
                      className={`chip-escolha${bipadoPelaTransportadora === true ? " marcada" : ""}`}
                      onClick={() => setBipadoPelaTransportadora(true)}
                    >
                      Sim, bipou mas não levou
                    </button>
                    <button
                      type="button"
                      className={`chip-escolha${bipadoPelaTransportadora === false ? " marcada" : ""}`}
                      onClick={() => setBipadoPelaTransportadora(false)}
                    >
                      Não chegou a bipar
                    </button>
                  </div>

                  <h3 className="rotulo">Observação (opcional)</h3>
                  <textarea
                    className="campo-texto"
                    placeholder="O que aconteceu"
                    value={observacao}
                    maxLength={240}
                    rows={2}
                    onChange={(e) => setObservacao(e.target.value)}
                  />

                  <div className="acoes">
                    <button
                      className="primario"
                      disabled={salvando || bipadoPelaTransportadora === null}
                      onClick={() => void confirmar()}
                    >
                      {salvando ? "Liberando…" : "Confirmar"}
                    </button>
                    <button disabled={salvando} onClick={() => setEscolhido(null)}>
                      Voltar
                    </button>
                  </div>
                </>
              )}

              {!escolhido && (
                <div className="acoes">
                  <button onClick={() => setAberto(false)}>Fechar</button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

      {camera && (
        <LeitorDeTransferencia
          dica="Escaneie o pacote que ficou pra trás"
          onFechar={() => setCamera(false)}
          onLido={(code) => {
            setCamera(false);
            void biparPacote(code);
          }}
        />
      )}
    </>
  );
}

/// As coletas que já saíram hoje.
///
/// Fica embaixo dos contadores porque responde a outra metade da mesma
/// pergunta: em cima está o que ainda vai sair, aqui o que já foi. Sem isto,
/// um contador zerado no celular não diria se a coleta foi fechada ou se o dia
/// ainda não começou.
function JaSairam({ coletas, onLiberado }: { coletas: ColetaFechada[]; onLiberado: () => void }) {
  if (coletas.length === 0) return null;

  const hora = (iso: string) =>
    new Date(iso).toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <>
      <h2 className="section">Coletas finalizadas hoje</h2>
      <div className="coletas-fechadas">
        {coletas.map((c) => (
          <div key={c.id} className="coleta-fechada">
            <div className="cabeca">
              <strong>{hora(c.fechadaEm)}</strong>
              <span className="pacotes">{c.pacotes}</span>
            </div>
            <div className="quem">por {c.fechadaPor}</div>
            {c.porPlataforma.length > 0 && (
              <div className="divisao">
                {c.porPlataforma.map((p) => `${p.plataforma} ${p.pacotes}`).join(" · ")}
              </div>
            )}
            {c.observacao && <p className="nota">{c.observacao}</p>}
            <LiberarPacoteDeColeta coletaId={c.id} onLiberado={onLiberado} />
          </div>
        ))}
      </div>
    </>
  );
}

export function Bipar() {
  const [coleta, setColeta] = useState<ColetaDoOperador | null>(null);
  const [fechadas, setFechadas] = useState<ColetaFechada[]>([]);
  const [transportadoras, setTransportadoras] = useState<ColetaDeTransportadora[]>([]);
  const [camera, setCamera] = useState(false);
  const [params, setParams] = useSearchParams();
  const batch = useBatch();

  const recarregar = useCallback(async () => {
    const [minha, doDia] = await Promise.all([
      api.minhaColeta(),
      api.coletas().catch(() => null),
    ]);
    setColeta(minha);
    if (doDia) {
      setFechadas(doDia.fechadas);
      setTransportadoras(doDia.aberta.porTransportadora);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  // Reconfere de tempos em tempos: o total inclui o que os outros bipam, e
  // esta tela fica aberta na mão de quem está esperando o número parar de
  // subir para conferir com o motorista.
  useEffect(() => {
    const t = setInterval(() => void recarregar(), 20_000);
    return () => clearInterval(t);
  }, [recarregar]);

  // Atalho da tela inicial do celular: /?camera=1 abre direto na câmera. O
  // parâmetro sai em seguida para que fechar a câmera não a reabra.
  useEffect(() => {
    if (params.get("camera") === "1") {
      armAudio();
      setCamera(true);
      params.delete("camera");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const fechar = () => {
    setCamera(false);
    void recarregar();
  };

  const meus = coleta?.meus ?? [];
  const outros = (coleta?.total ?? 0) - (coleta?.meuTotal ?? 0);

  return (
    <>
      <main>
        <h2 className="section">Pacotes por conta hoje</h2>
        {meus.length > 0 ? (
          <div className="accounts">
            {meus.map((t) => (
              <div
                key={t.accountId ?? "sem"}
                className="account-card"
                style={{ ["--chip" as string]: t.color ?? "var(--dep-border)" }}
              >
                <span className="platform">{t.platformName ?? "—"}</span>
                <span className="name">{t.accountName}</span>
                <span className="count">{t.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty">Você ainda não bipou nada nesta coleta.</p>
        )}

        {/* O total da casa, embaixo do que é meu.
            Duas linhas e não uma: o número de cima é o meu trabalho, o de
            baixo é o que vai ser conferido com o motorista. Um número só
            deixaria a pessoa achando que bipou o dobro do que bipou. */}
        <div className="totais">
          <div className="linha">
            <span>Você bipou</span>
            <strong>{coleta?.meuTotal ?? 0}</strong>
          </div>
          {outros > 0 && (
            <div className="linha muda">
              <span>Os outros</span>
              <strong>{outros}</strong>
            </div>
          )}
          <div className="linha total">
            <span>Total da coleta</span>
            <strong>{coleta?.total ?? 0}</strong>
          </div>
        </div>

        {/* Quem são "os outros", igual no painel de Bipagens: lá cada leitura
            tem uma coluna de Operador e uma de Conta lado a lado. Aqui não
            cabe tabela, então vira cartão — pessoa e conta juntas, como
            "Renato · Mercado Livre". Sem isto, "os outros: 12" não dizia se
            eram doze do mesmo colega adiantado ou três pessoas espalhadas. */}
        {coleta && coleta.outros.length > 0 && (
          <>
            <h2 className="section">Quem mais bipou</h2>
            <div className="accounts">
              {coleta.outros.map((o) => (
                <div
                  key={`${o.userId}:${o.accountId ?? "sem"}`}
                  className="account-card"
                  style={{ ["--chip" as string]: o.color ?? "var(--dep-border)" }}
                >
                  <span className="platform">{o.platformName ?? "—"}</span>
                  <span className="name">
                    {o.userName} · {o.accountName}
                  </span>
                  <span className="count">{o.count}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <Finalizar
          transportadoras={transportadoras}
          total={coleta?.total ?? 0}
          aoFechar={() => void recarregar()}
        />

        {batch.items.length > 0 && (
          <>
            <h2 className="section">Lote em conferência · {batch.items.length}</h2>
            <p className="note">
              Estes pacotes <b>ainda não foram lançados</b>. Abra a câmera e toque em
              Entregar para confirmar o lote.
            </p>
          </>
        )}

        <JaSairam coletas={fechadas} onLiberado={recarregar} />

        <p className="note">
          Toque no botão para abrir a câmera. Cada leitura é conferida na hora, mas só entra
          no sistema quando você apertar <b>Entregar</b>. A contagem zera sozinha quando a
          coleta é finalizada.
        </p>
      </main>

      <button
        className="scan-fab"
        aria-label="Abrir câmera para bipar"
        onClick={() => {
          armAudio();
          setCamera(true);
        }}
      >
        <QrIcon />
      </button>

      {camera && (
        <ScanScreen
          direction="OUTBOUND"
          reason={null}
          titulo="Escaneie o código"
          onClose={fechar}
        />
      )}
    </>
  );
}
